// src/components/dashboard/JugCard.jsx
import React, { useMemo, useCallback } from "react";
import { Badge } from "@radix-ui/themes";
import { useJugImageLogic } from "../../../hooks/useJugImageLogic";

const JugCard = ({
  jug,
  isSelected,
  onSelection,
  onPayment,
  canSelect = false,
  isEligible = false,
  isInRefillPair = false,
}) => {
  const { getJugImage } = useJugImageLogic();
  const jugImagePath = getJugImage(jug);

  // Mapeo simplificado usando los campos de la vista actualizada
  const jugConfig = useMemo(() => {
    const status = jug.order_detail_status;
    const alertType = jug.alert_type;
    const alertPriority = jug.alert_priority;
    const daysInUse = jug.days_since_delivery;
    const paymentStatus = jug.payment_status;
    const scheduledDate = jug.scheduled_delivery_date;
    const needsPayment =
      paymentStatus === "pending" || paymentStatus === "failed";

    // Calcular tiempo estimado para pedidos activos
    const getEstimatedTime = () => {
      if (!scheduledDate) return null;

      const now = new Date();
      const scheduled = new Date(scheduledDate);
      const diffMs = scheduled.getTime() - now.getTime();
      const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

      return diffHours > 0 ? diffHours : null;
    };

    const estimatedHours = getEstimatedTime();

    // Estados para botellones entregados (usando alertType de la vista)
    if (status === "delivered") {
      // Mapeo directo de alert_type a configuración visual
      const alertConfigs = {
        mandatory_return: {
          label: "Retorno necesario",
          bgColor: "bg-red-700",
          textColor: "text-orange-100",
          canSelect: true,
          bottleOpacity: 0,
        },
        return_required: {
          label: "Para retornar",
          bgColor: "bg-neutral-700",
          textColor: "text-neutral-200",
          canSelect: true,
          bottleOpacity: 20,
        },
        expiration_warning: {
          label: "Recambio sugerido",
          bgColor: "bg-amber-500",
          textColor: "text-orange-100",
          canSelect: true,
          bottleOpacity: 30,
        },
      };

      const alertConfig = alertConfigs[alertType];

      if (alertConfig) {
        return {
          ...alertConfig,
          timeTag: `${daysInUse}d en uso`,
          timeTagColor: "bg-zinc-100 text-neutral-800",
          // LÓGICA DE SELECCIÓN ACTUALIZADA: usar isEligible y !isInRefillPair
          showSelector: isEligible && !isInRefillPair,
          showPayButton: false,
        };
      }

      // Entregado reciente (sin alerta)
      return {
        label: "Entregado",
        bgColor: "bg-green-100",
        textColor: "text-green-600",
        canSelect: isEligible && !isInRefillPair, // LÓGICA ACTUALIZADA
        timeTag: `${daysInUse}d en uso`,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showSelector: isEligible && !isInRefillPair, // LÓGICA ACTUALIZADA
        showPayButton: false,
        bottleOpacity: 100,
      };
    }

    // Estados de producción y entrega (usando status directamente)
    const productionStates = {
      recently_ordered: {
        label: "Recién pedido",
        bgColor: "bg-gray-200",
        textColor: "text-neutral-500",
        canSelect: false,
        showPayButton: needsPayment,
        timeTag: needsPayment
          ? "Sin pago"
          : estimatedHours
          ? `Llega en ${estimatedHours}h`
          : null,
        timeTagColor: needsPayment
          ? "bg-red-700 text-rose-200"
          : "bg-zinc-100 text-neutral-800",
        showSelector: false,
        bottleOpacity: needsPayment ? 40 : 100,
      },
      ready_for_production: {
        label: "En cola de producción",
        bgColor: "bg-sky-100",
        textColor: "text-blue-600",
        canSelect: false,
        timeTag: estimatedHours ? `Llega en ${estimatedHours}h` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showSelector: false,
        showPayButton: false,
        bottleOpacity: 100,
      },
      in_washing: {
        label: "Sanitización",
        bgColor: "bg-sky-100",
        textColor: "text-blue-600",
        canSelect: false,
        timeTag: estimatedHours ? `Llega en ${estimatedHours}h` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showSelector: false,
        showPayButton: false,
        bottleOpacity: 100,
      },
      in_filling: {
        label: "Llenado y sellado",
        bgColor: "bg-sky-100",
        textColor: "text-blue-600",
        canSelect: false,
        timeTag: estimatedHours ? `Llega en ${estimatedHours}h` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showSelector: false,
        showPayButton: false,
        bottleOpacity: 100,
      },
      ready_for_delivery: {
        label: "En camino",
        bgColor: "bg-sky-100",
        textColor: "text-blue-600",
        canSelect: false,
        timeTag: estimatedHours ? `Llega en ${estimatedHours}h` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showSelector: false,
        showPayButton: false,
        bottleOpacity: 100,
      },
      in_transit: {
        label: "En camino",
        bgColor: "bg-sky-100",
        textColor: "text-blue-600",
        canSelect: false,
        timeTag: estimatedHours ? `Llega en ${estimatedHours}h` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showSelector: false,
        showPayButton: false,
        bottleOpacity: 100,
      },
      to_be_refilled: {
        label: "Para recambio",
        bgColor: "bg-yellow-100",
        textColor: "text-yellow-700",
        canSelect: isEligible && !isInRefillPair, // LÓGICA ACTUALIZADA
        timeTag: daysInUse ? `${daysInUse}d en uso` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showSelector: isEligible && !isInRefillPair, // LÓGICA ACTUALIZADA
        showPayButton: false,
        bottleOpacity: 100,
      },
      to_be_returned: {
        label: "Para retornar",
        bgColor: "bg-neutral-700",
        textColor: "text-neutral-200",
        canSelect: false, // NO SELECCIONABLE según nuevas reglas
        timeTag: daysInUse ? `${daysInUse}d en uso` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showSelector: false, // NO SELECCIONABLE según nuevas reglas
        showPayButton: false,
        bottleOpacity: 40,
      },
      overdue_for_return: {
        label: "Vencido",
        bgColor: "bg-red-700",
        textColor: "text-orange-100",
        canSelect: isEligible && !isInRefillPair, // LÓGICA ACTUALIZADA
        timeTag: daysInUse ? `${daysInUse}d en uso` : null,
        timeTagColor: "bg-zinc-100 text-neutral-800",
        showSelector: isEligible && !isInRefillPair, // LÓGICA ACTUALIZADA
        showPayButton: false,
        bottleOpacity: 100,
      },
    };

    return (
      productionStates[status] || {
        label: "Desconocido",
        bgColor: "bg-gray-200",
        textColor: "text-neutral-500",
        canSelect: false,
        showSelector: false,
        showPayButton: false,
        bottleOpacity: 100,
      }
    );
  }, [jug, isEligible, isInRefillPair]); // Agregar dependencias

  // ID del botellón (usar barcode o short_id desde la vista)
  const jugId =
    jug.barcode ||
    jug.detail_short_id ||
    `${jug.jug_id?.slice(-8) || "XXXX-XXXX"}`;

  // Formatear monto para mostrar en caso de pago pendiente
  const formatAmount = useCallback((amount) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount);
  }, []);

  // Handlers - LÓGICA DE SELECCIÓN ACTUALIZADA
  const handleCardClick = () => {
    // Solo permitir selección si es elegible y no está en par de refill
    if (jugConfig.canSelect && canSelect && isEligible && !isInRefillPair) {
      onSelection?.(jug.jug_id, true);
    }
  };

  const handlePayClick = (e) => {
    e.stopPropagation();
    onPayment?.(jug.jug_id, jug.detail_total || jug.order_total);
  };

  return (
    <div
      className={`
        w-48 p-4 relative bg-white rounded-2xl inline-flex flex-col justify-center items-center gap-4
        transition-all duration-200
        ${
          isSelected
            ? "bg-green-500 outline outline-2 outline-offset-[-2px] outline-blue-500"
            : ""
        }
        ${
          jugConfig.canSelect && canSelect && isEligible && !isInRefillPair
            ? "cursor-pointer"
            : ""
        }
      `}
      onClick={handleCardClick}
      title={jug.alert_message} // Tooltip con mensaje de alerta de la vista
    >
      {/* Botón de pagar (esquina superior derecha) */}
      {jugConfig.showPayButton && (
        <button
          onClick={handlePayClick}
          className="h-8 max-h-8 min-h-8 px-3 left-[108px] top-[16px] absolute bg-blue-600 rounded-xl inline-flex justify-center items-center gap-1 hover:bg-blue-700 transition-colors z-10"
          title={`Pagar ${formatAmount(jug.detail_total || jug.order_total)}`}
        >
          <span className="text-zinc-100 text-base font-medium leading-none">
            Pagar
          </span>
        </button>
      )}

      {/* Selector de botellón (esquina superior derecha) - LÓGICA ACTUALIZADA */}
      {jugConfig.showSelector &&
        jugConfig.canSelect &&
        canSelect &&
        isEligible &&
        !isInRefillPair && (
          <div
            className={`
          w-8 h-8 p-1.5 right-[16px] top-[16px] absolute rounded-[999px] outline outline-2 outline-offset-[-2px] 
          ${isSelected ? "outline-blue-600" : "outline-neutral-600"} 
          inline-flex justify-start items-center gap-2.5 z-10
        `}
          >
            <div
              className={`
            flex-1 self-stretch rounded-full transition-colors
            ${isSelected ? "bg-blue-800" : "bg-zinc-100"}
          `}
            />
          </div>
        )}

      {/* Imagen del botellón con opacidad dinámica */}
      <div
        className={`w-fit h-fit   transition-opacity duration-300 ${
          jugConfig.bottleOpacity < 100
            ? `opacity-${jugConfig.bottleOpacity}`
            : ""
        }`}
      >
        <img
          className="w-32 h-32 md:min-w-56 md:min-h-56"
          src={jugImagePath}
          alt={`Botellón ${jugId}`}
          loading="lazy"
        />
      </div>

      {/* Información del botellón */}
      <div className="self-stretch flex flex-col justify-center items-center gap-2">
        {/* Badge de estado */}
        <div
          className={`
          px-2 py-1 ${jugConfig.bgColor} rounded-lg inline-flex justify-center items-center gap-2.5
          transition-colors duration-200
        `}
        >
          <span
            className={`text-sm font-medium leading-none ${jugConfig.textColor}`}
          >
            {jugConfig.label}
          </span>
        </div>

        {/* ID y tag de tiempo */}
        <div className="inline-flex justify-start items-center gap-1 overflow-hidden">
          <span className="text-black text-base font-medium leading-none">
            {jugId}
          </span>

          {jugConfig.timeTag && (
            <div
              className={`
              px-1 py-0.5 rounded flex justify-center items-center gap-2.5 
              ${jugConfig.timeTagColor} transition-colors duration-200
            `}
            >
              <span className="text-center text-xs  font-medium ">
                {jugConfig.timeTag}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(JugCard);
